import { z } from 'zod'
import { baseModelShape } from '../baseModel'

// Presentation half of a template, as exposed by the backend serializer.
export const achievementTemplateShape = {
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().optional(),
  bannerColor: z.string().optional(),
  icon: z.string().optional(),
  image: z.string().nullish(),
  tier: z.string().optional(),
  tierDisplay: z.string().optional(),
  pointValue: z.number().optional(),
}

// An achievement a fundraiser has earned.
export const fundraiserAchievementShape = {
  ...baseModelShape,
  periodKey: z.string().optional(),
  pointsAwarded: z.number().optional(),
  tier: z.string().optional(),
  seen: z.boolean().optional(),
  template: z.object(achievementTemplateShape),
}

/**
 * Convert a raw catalog row (all achievements, earned + locked) into the
 * camelCase shape the journey path expects.
 */
export const normalizeCatalogItem = (raw) => ({
  templateId: raw.template_id,
  track: raw.track,
  name: raw.name,
  description: raw.description,
  bannerColor: raw.banner_color || '#B57E14',
  icon: raw.icon || 'TrophyIcon',
  image: raw.image || null,
  tier: raw.tier,
  tierDisplay: raw.tier_display,
  pointValue: raw.point_value ?? 0,
  requirement: raw.requirement,
  earned: !!raw.earned,
  earnedAt: raw.earned_at,
  pointsAwarded: raw.points_awarded ?? 0,
})

export const normalizeStatesMap = (raw) => ({
  total: raw.total ?? 0,
  unlockedCount: raw.unlocked_count ?? 0,
  states: (raw.states || []).map((s) => ({
    code: s.code,
    name: s.name,
    unlocked: !!s.unlocked,
    givarooId: s.givaroo_id ?? null,
    unlockedAt: s.unlocked_at ?? null,
  })),
})

export const normalizeStateUnlock = (raw) => ({
  id: raw.id,
  kind: 'state',
  stateCode: raw.state_code,
  stateName: raw.state_name,
  givarooId: raw.givaroo_id,
  unlockedAt: raw.unlocked_at,
  // Shape compatible with the achievement celebration card.
  template: {
    name: raw.state_name,
    description: `First donor signed up from ${raw.state_name}`,
    bannerColor: '#2F7D6B',
    icon: 'MapIcon',
    image: null,
    tierDisplay: raw.state_code,
  },
  pointsAwarded: 0,
})

/**
 * Convert a raw (snake_case) achievement row from the API into the camelCase
 * shape the components expect. Defensive against missing nested template.
 */
export const normalizeAchievement = (raw) => ({
  id: raw.id,
  periodKey: raw.period_key,
  pointsAwarded: raw.points_awarded,
  tier: raw.tier,
  seen: raw.seen,
  datetimeCreated: raw.datetime_created,
  template: {
    id: raw.template?.id,
    name: raw.template?.name,
    description: raw.template?.description,
    bannerColor: raw.template?.banner_color || '#B57E14',
    icon: raw.template?.icon || 'TrophyIcon',
    image: raw.template?.image || null,
    tier: raw.template?.tier,
    tierDisplay: raw.template?.tier_display,
    pointValue: raw.template?.point_value ?? 0,
  },
})
